import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'
import {
  Book,
  Shield,
  MessageSquare,
  Smartphone,
  Webhook,
  AlertTriangle,
  Zap,
  ChevronRight,
  Search,
  Menu,
  X
} from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'

const navigation = [
  {
    title: 'Getting Started',
    icon: Book,
    items: [
      { title: 'Introduction', href: '/docs' },
      { title: 'Quick Start', href: '/docs/quickstart' },
      { title: 'Authentication', href: '/docs/authentication' },
    ]
  },
  {
    title: 'API Reference',
    icon: MessageSquare,
    items: [
      { title: 'Send SMS', href: '/docs/api/send-sms' },
      { title: 'Send Bulk SMS', href: '/docs/api/send-bulk-sms' },
      { title: 'Get Messages', href: '/docs/api/get-messages' },
      { title: 'Get Message', href: '/docs/api/get-message' },
    ]
  },
  {
    title: 'Devices',
    icon: Smartphone,
    items: [
      { title: 'List Devices', href: '/docs/api/list-devices' },
      { title: 'Get Device', href: '/docs/api/get-device' },
      { title: 'Update Device', href: '/docs/api/update-device' },
      { title: 'Delete Device', href: '/docs/api/delete-device' },
    ]
  },
  {
    title: 'Webhooks',
    icon: Webhook,
    items: [
      { title: 'Overview', href: '/docs/webhooks' },
      { title: 'Event Types', href: '/docs/webhooks/events' },
      { title: 'Security', href: '/docs/webhooks/security' },
      { title: 'Testing', href: '/docs/webhooks/testing' },
    ]
  },
  {
    title: 'Guides',
    icon: Shield,
    items: [
      { title: 'Error Handling', href: '/docs/guides/errors' },
      { title: 'Rate Limits', href: '/docs/guides/rate-limits' },
      { title: 'Pagination', href: '/docs/guides/pagination' },
      { title: 'Best Practices', href: '/docs/guides/best-practices' },
    ]
  },
  {
    title: 'Resources',
    icon: AlertTriangle,
    items: [
      { title: 'Error Reference', href: '/docs/errors' },
      { title: 'Status Codes', href: '/docs/status-codes' },
      { title: 'SDKs & Libraries', href: '/docs/sdks' },
      { title: 'Changelog', href: '/docs/changelog' },
    ]
  },
]

export default function DocsLayout() {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 w-full backdrop-blur flex-none border-b border-gray-200 bg-white/95">
        <div className="max-w-8xl mx-auto">
          <div className="py-4 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-8">
                <Link to="/" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">T</span>
                  </div>
                  <span className="font-semibold text-gray-900">redgeGate Docs</span>
                </Link>

                <div className="hidden md:flex items-center gap-6 text-sm">
                  <Link to="/docs" className="text-gray-700 hover:text-gray-900">Documentation</Link>
                  <Link to="/docs/api/send-sms" className="text-gray-700 hover:text-gray-900">API Reference</Link>
                  <Link to="/docs/guides/errors" className="text-gray-700 hover:text-gray-900">Guides</Link>
                  <Link to="/dashboard" className="text-gray-700 hover:text-gray-900">Dashboard</Link>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="hidden md:block">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search docs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8 py-8">
          {/* Sidebar */}
          <aside className={`
            ${sidebarOpen ? 'block' : 'hidden'} md:block
            w-64 shrink-0
          `}>
            <nav className="sticky top-24 space-y-8">
              {navigation.map((section) => {
                const Icon = section.icon
                return (
                  <div key={section.title}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="h-4 w-4 text-gray-600" />
                      <h3 className="font-semibold text-gray-900 text-sm">{section.title}</h3>
                    </div>
                    <ul className="space-y-2 border-l border-gray-200">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.href
                        return (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              className={`
                                flex items-center gap-2 pl-4 py-1.5 text-sm border-l-2 -ml-px
                                ${isActive
                                  ? 'border-blue-600 text-blue-600 font-medium'
                                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                                }
                              `}
                            >
                              {item.title}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="max-w-4xl">
              <Outlet />
            </div>
          </main>

          {/* Right Sidebar - Table of Contents */}
          <aside className="hidden xl:block w-64 shrink-0">
            <div className="sticky top-24">
              <h3 className="font-semibold text-gray-900 text-sm mb-4">On this page</h3>
              <nav className="space-y-2 text-sm text-gray-600">
                {/* This will be dynamically populated based on page content */}
              </nav>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}
